import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";

/** Field label: 12px, medium, sentence case, muted. */
export function Label({ children, htmlFor, className = "" }: { children: ReactNode; htmlFor?: string; className?: string }) {
  return (
    <label htmlFor={htmlFor} className={`mb-1.5 block text-xs font-medium text-muted ${className}`}>
      {children}
    </label>
  );
}

// On phones controls grow to the 44px tap minimum and 16px type - iOS Safari
// zooms the page into any focused field set smaller than 16px.
const control =
  "w-full rounded-md border border-line bg-input text-[13px] text-ink placeholder:text-faint outline-none transition duration-150 " +
  "focus:border-accent focus:shadow-glow aria-invalid:border-danger disabled:opacity-50 max-[899px]:text-base";

export function Input({ className = "", invalid, ...props }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return <input {...props} aria-invalid={invalid || undefined} className={`${control} h-[38px] px-3 max-[899px]:h-11 ${className}`} />;
}

export function Select({ className = "", children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative w-full">
      <select {...props} className={`${control} h-[38px] appearance-none pl-3 pr-9 max-[899px]:h-11 ${className}`}>
        {children}
      </select>
      <svg viewBox="0 0 16 16" width="14" height="14" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M4 6l4 4 4-4" />
      </svg>
    </div>
  );
}

/** Multi-line input; `max` shows a live counter that turns amber at the limit. */
export function Textarea({ className = "", max, value = "", rows = 3, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { max?: number; value?: string }) {
  const length = String(value).length;
  return (
    <div className="relative">
      <textarea
        {...props}
        value={value}
        rows={rows}
        maxLength={max}
        className={`${control} block resize-y px-3 py-2.5 leading-normal ${max ? "pb-6" : ""} ${className}`}
      />
      {max && (
        <span className={`tabular pointer-events-none absolute bottom-2.5 right-3 text-[11px] ${length >= max ? "text-warn" : "text-faint"}`}>
          {length} / {max}
        </span>
      )}
    </div>
  );
}

/** 36×22 switch with label and hint. */
export function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative mt-px h-[22px] w-9 shrink-0 rounded-full transition duration-150 ${checked ? "bg-accent" : "bg-glass-2 shadow-[inset_0_0_0_1px_var(--color-line-strong)]"}`}
      >
        <span className={`absolute top-[3px] h-4 w-4 rounded-full bg-white transition-[left] duration-150 ${checked ? "left-[17px]" : "left-[3px]"}`} />
      </button>
      <span>
        <span className="block text-[13px] font-medium">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-muted">{hint}</span>}
      </span>
    </label>
  );
}

/** Inline error under a form; reserves a line so the layout never jumps. */
export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="min-h-5 text-[13px] text-danger">
      {children}
    </p>
  );
}
